import { AjaxError } from '@api/util';
import html from './template.html';

const template = document.createElement('template');
template.innerHTML = html;

/**
 * Banner element that responds on the scrolling of the document.
 * @extends	HTMLElement
 */
customElements.define(
    'brands-models',
    class BrandsModelsElement extends HTMLElement {
        /**
         * The API for the brands and models data.
         */
        static get api() {
            return {
                BRANDS_MODELS: '/wp-json/occasion-picker/v1/brandmodels',
                COUNT: '/wp-json/occasion-picker/v1/count',
            };
        }

        constructor() {
            super();

            const shadow = this.attachShadow({ mode: 'open' });
            shadow.append(template.content.cloneNode(true));

            /**
             * This creates a "store-like" object which enables us to run
             * functions whenever a certain key is changed.
             *
             * For example. Whenever we modify this.data.brands, the "set"
             * method will run and do the default behavior of setting a property
             * on an object with the additional features of calling our own functions.
             */
            this.data = new Proxy(
                {
                    brands: [],
                    count: 0,
                    selectedBrand: '',
                    selectedModel: '',
                    loadingCount: false,
                },
                {
                    set: (state, key, values) => {
                        Reflect.set(state, key, values);

                        switch (key) {
                            case 'brands':
                                this.renderBrandOptions();
                                this.disableModelSelects();
                                break;

                            case 'loadingCount':
                                if (values === true) {
                                    this.renderLoadingCount();
                                } else {
                                    this.renderCount();
                                }
                                break;

                            case 'count':
                                this.renderCount();
                                break;

                            case 'selectedBrand':
                                this.enableModelSelects();
                                this.renderModelOptions();
                                break;
                        }

                        return true;
                    },
                }
            );
        }

        /**
         * Returns the elements assigned to the brands slot.
         * @property
         */
        get brandsSlotElements() {
            const slot = this.shadowRoot.querySelector('slot[name=brands]');
            return slot.assignedElements();
        }

        /**
         * Returns the elements assigned to the models slot.
         * @property
         */
        get modelsSlotElements() {
            const slot = this.shadowRoot.querySelector('slot[name=models]');
            return slot.assignedElements();
        }

        /**
         * Returns the elements assigned to the submit slot.
         * @property
         */
        get submitSlotElements() {
            const slot = this.shadowRoot.querySelector('slot[name=submit]');
            return slot.assignedElements();
        }

        /**
         * Gets and sets the brand-label attribute.
         * @property
         */
        get brandLabel() {
            return this.getAttribute('brand-label');
        }

        set brandLabel(value) {
            if ('string' === typeof value) {
                this.setAttribute('brand-label', value);
            }
        }

        /**
         * Gets and sets the model-label attribute.
         * @property
         */
        get modelLabel() {
            return this.getAttribute('model-label');
        }

        set modelLabel(value) {
            if ('string' === typeof value) {
                this.setAttribute('model-label', value);
            }
        }

        /**
         * Gets and sets the loading-label attribute.
         * @property
         */
        get loadingLabel() {
            return this.getAttribute('loading-label');
        }

        set loadingLabel(value) {
            if ('string' === typeof value) {
                this.setAttribute('loading-label', value);
            }
        }

        /**
         * Fires when the element has been connected.
         *
         * @method	connectedCallback
         * @returns	{void}
         */
        async connectedCallback() {
            if (this.brandLabel === null || this.brandLabel === '') {
                this.brandLabel = 'Merk';
            }

            if (this.modelLabel === null || this.modelLabel === '') {
                this.modelLabel = 'Model';
            }

            if (this.loadingLabel === null || this.loadingLabel === '') {
                this.loadingLabel = "Zoeken naar auto's..";
            }

            for (const select of this.brandsSlotElements) {
                select.addEventListener('change', ({ target }) => {
                    this.data.selectedBrand = target.value;
                    this.data.selectedModel = '';

                    if (target.value === '') {
                        this.disableModelSelects();
                    }

                    this.data.loadingCount = true;
                    this.getOccasionsCount({
                        model: this.data.selectedModel,
                        brand: this.data.selectedBrand,
                    })
                        .then(count => {
                            this.data.count = count.toLocaleString();
                        })
                        .finally(() => {
                            this.data.loadingCount = false;
                        });
                });
            }

            for (const select of this.modelsSlotElements) {
                select.addEventListener('change', ({ target }) => {
                    this.data.selectedModel = target.value;
                    this.data.loadingCount = true;
                    this.getOccasionsCount({
                        model: this.data.selectedModel,
                        brand: this.data.selectedBrand,
                    })
                        .then(count => {
                            this.data.count = count.toLocaleString();
                        })
                        .finally(() => {
                            this.data.loadingCount = false;
                        });
                });
            }

            this.data.loadingCount = true;
            Promise.all([this.getBrandsAndModels(), this.getOccasionsCount()])
                .then(([data, count]) => {
                    this.data.brands = data;
                    this.data.count = count.toLocaleString();
                })
                .finally(() => {
                    this.data.loadingCount = false;
                });
        }

        /**
         * Fires when the element has been disconnected.
         *
         * @method	disconnectedCallback
         * @returns	{void}
         */
        disconnectedCallback() {}

        /**
         * Get the brands and models from the API
         *
         * @returns {object}
         */
        async getBrandsAndModels() {
            try {
                const response = await fetch(
                    BrandsModelsElement.api.BRANDS_MODELS
                );

                if (!response.ok) {
                    throw new AjaxError(
                        `Fetching data from ${BrandsModelsElement.api.BRANDS_MODELS} caused an error.`,
                        response
                    );
                }

                const { status, data } = await response.json();
                if (status === 'success') {
                    return data;
                }

                return {};
            } catch (error) {
                console.warn(error);
            }
        }

        /**
         * Get the amount of occasions found.
         *
         * @param {object} options
         * @returns {number}
         */
        async getOccasionsCount({ brand = null, model = null } = {}) {
            const body = {
                id: null,
                filters: {
                    facets: {
                        Brand: {
                            Values: brand ? [brand] : [],
                        },
                        Model: {
                            Values: model ? [model] : [],
                        },
                    },
                },
            };

            try {
                const response = await fetch(BrandsModelsElement.api.COUNT, {
                    method: 'POST',
                    body: JSON.stringify(body),
                });

                if (!response.ok) {
                    throw new AjaxError(
                        `Fetching data from ${BrandsModelsElement.api.COUNT} caused an error.`,
                        response
                    );
                }

                const { data } = await response.json();
                return data.count;
            } catch (error) {
                console.warn(error);
            }
        }

        /**
         * Create options and add them to the select element.
         */
        renderBrandOptions() {
            const selects = this.brandsSlotElements;
            const params = new URLSearchParams(location.search);

            const selectedBrand = params.get('Brand');

            for (const select of selects) {
                // First remove all options.
                select.innerHTML = '';

                const firstOption = new Option('Kies een merk', '', true, true);
                // firstOption.disabled = true
                select.add(firstOption);

                // Add the new options.
                for (const { _id: brand } of this.data.brands) {
                    const option = new Option(
                        brand,
                        brand,
                        false,
                        selectedBrand === brand // Adds selected attribute if brand is a match
                    );
                    select.add(option);
                }
            }
        }

        /**
         * Create options for the model select element.
         */
        renderModelOptions() {
            const selects = this.modelsSlotElements;

            for (const select of selects) {
                // First remove all options.
                select.innerHTML = '';

                // Label based on if the brand is selected.
                const label =
                    this.data.selectedBrand === ''
                        ? 'Kies eerst een merk'
                        : this.modelLabel;

                const firstOption = new Option(label, '', true, true);
                select.add(firstOption);

                // Get the models that are associated with the brand.
                const brand = this.data.brands.find(
                    ({ _id: brand }) => brand === this.data.selectedBrand
                );

                if (brand === undefined) {
                    return;
                }

                // Add the new options.
                for (const model of brand.Models.values()) {
                    const option = new Option(model, model);
                    select.add(option);
                }
            }
        }

        /**
         * Shows a loading message in the button to indicate that it's making a request.
         */
        renderLoadingCount() {
            const buttons = this.submitSlotElements;

            for (const button of buttons) {
                button.textContent = this.loadingLabel;
                button.disabled = true;
            }
        }

        /**
         * Render the count amount of amount of occassions found in the button element.
         */
        renderCount() {
            const buttons = this.submitSlotElements;

            for (const button of buttons) {
                button.textContent = `${button.dataset.label} (${this.data.count})`;
                button.disabled = false;
            }
        }

        /**
         * Disables all selects in the brand slot
         */
        disableBrandSelects() {
            for (const select of this.brandsSlotElements) {
                select.disabled = true;
            }
        }

        /**
         * Disables all selects in the model slot
         */
        disableModelSelects() {
            for (const select of this.modelsSlotElements) {
                select.disabled = true;
            }
        }

        /**
         * Enables all selects in the brand slot
         */
        enableBrandSelects() {
            for (const select of this.brandsSlotElements) {
                select.disabled = false;
            }
        }

        /**
         * Enables all selects in the model slot
         */
        enableModelSelects() {
            for (const select of this.modelsSlotElements) {
                select.disabled = false;
            }
        }
    }
);



// WEBPACK FOOTER //
// ./scripts/web-components/brands-models/index.js